import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDownIcon, ArrowUpIcon } from 'lucide-react';
import { curatedModels } from '../../data/models';
import { cca2Of, countryNames } from '../../data/geography';
import { CuratedModel, License, Modality } from '../../types/content';
import { formatReleased } from '../../utils/format';
import { modelCountry } from '../../utils/ecosystem';
import { AccessFilter, curatedMatches } from '../../utils/modelFilters';
import { SourceRef } from '../SourceRef';
import { ChipGroup } from '../ChipGroup';
import { DownloadButtons } from '../DownloadButtons';
type SortKey = 'model' | 'lab' | 'country' | 'released' | 'context';
type ModalityFilter = 'all' | Modality;
const LICENSE_STYLE: Record<License, string> = {
  open: 'border-live/40 text-live',
  'open-weights': 'border-human/40 text-human',
  proprietary: 'border-line text-muted'
};
const LICENSE_LABEL: Record<License, string> = {
  open: 'Open',
  'open-weights': 'Open weights',
  proprietary: 'Proprietary'
};
function contextTokens(c: string): number {
  const m = c.match(/([\d.]+)\s*([KM])/);
  if (!m) return -1;
  return Number(m[1]) * (m[2] === 'M' ? 1e6 : 1e3);
}
const sorters: Record<SortKey, (a: CuratedModel, b: CuratedModel) => number> = {
  model: (a, b) => a.model.localeCompare(b.model),
  lab: (a, b) => a.lab.localeCompare(b.lab),
  country: (a, b) => (countryNames[modelCountry(a) ?? ''] ?? 'zz').localeCompare(countryNames[modelCountry(b) ?? ''] ?? 'zz'),
  released: (a, b) => (a.released ?? '0000').localeCompare(b.released ?? '0000'),
  context: (a, b) => contextTokens(a.context) - contextTokens(b.context)
};
export function CuratedModelTable({
  access,
  country
}: {
  access: AccessFilter;
  country: string;
}) {
  const labs = useMemo(() => Array.from(new Set(curatedModels.map(m => m.lab))).sort(), []);
  const [lab, setLab] = useState('all');
  const [modality, setModality] = useState<ModalityFilter>('all');
  const [sortKey, setSortKey] = useState<SortKey>('released');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const rows = useMemo(() => {
    const list = curatedModels.filter(m => (lab === 'all' || m.lab === lab) && curatedMatches(m, access) && (country === 'all' || modelCountry(m) === country) && (modality === 'all' || m.modality === modality));
    const s = [...list].sort(sorters[sortKey]);
    return dir === 'asc' ? s : s.reverse();
  }, [lab, access, country, modality, sortKey, dir]);
  const sortBy = (k: SortKey) => {
    if (k === sortKey) setDir(d => d === 'asc' ? 'desc' : 'asc');else {
      setSortKey(k);
      setDir(k === 'released' || k === 'context' ? 'desc' : 'asc');
    }
  };
  const sortTh = (k: SortKey, label: string) => <th key={k} scope="col" aria-sort={sortKey === k ? dir === 'asc' ? 'ascending' : 'descending' : 'none'} className="whitespace-nowrap border-b border-line px-3 py-2.5 text-left font-normal" data-id="element-516" data-mp="n9cdcxqr0doj">
      <button type="button" onClick={() => sortBy(k)} className={`inline-flex items-center gap-1 text-[12px] ${sortKey === k ? 'text-ink' : 'text-muted hover:text-ink'}`} data-id="element-517" data-mp="2txl3ugzcs7jk">
        {label}
        {sortKey === k ? dir === 'asc' ? <ArrowUpIcon className="h-3 w-3" data-id="element-518" data-mp="33waqen272g65" /> : <ArrowDownIcon className="h-3 w-3" data-id="element-519" data-mp="33t4y6yy9qy67" /> : <span className="w-3" data-id="element-520" data-mp="37xcqf63xqw96" />}
      </button>
    </th>;
  return <div data-id="element-521" data-mp="37ui4tb96r5e6">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 pb-4" data-id="element-522" data-mp="2b6k4vsy8gc00">
        <label className="flex items-center gap-2 text-[13px] text-muted" data-id="element-523" data-mp="2b4h90saefond">
          Lab
          <select value={lab} onChange={e => setLab(e.target.value)} className="rounded-md border border-line bg-surface px-2.5 py-1.5 text-[13px] text-ink focus:border-machine focus:outline-none" data-id="element-524" data-mp="2g5ws4et0nl34">
            <option value="all" data-id="element-525" data-mp="2oz9vkaopi53g">All labs</option>
            {labs.map(l => <option key={l} value={l} data-id="element-526" data-mp="1sfau7gcq7h6u">
                {l}
              </option>)}
          </select>
        </label>
        <ChipGroup label="Modality" value={modality} onChange={setModality} options={[['all', 'All'], ['text', 'Text'], ['multimodal', 'Multimodal'], ['code', 'Code']]} data-id="element-527" data-mp="1wfbbd8r7m6hl" />
        <span className="ml-auto flex items-center gap-4" data-id="element-528" data-mp="18fj2ccf836yz">
          <span className="font-mono text-[12px] text-muted" aria-live="polite" data-id="element-529" data-mp="1n3dxs0km7pfr">
            {rows.length} shown
          </span>
          <DownloadButtons name="models" getRows={() => rows.map(m => ({
          model: m.model,
          lab: m.lab,
          country: countryNames[modelCountry(m) ?? ''] ?? null,
          released: m.released,
          params: m.params,
          context: m.context,
          license: m.licenseName,
          modality: m.modality,
          capability: m.capability,
          source: m.sourceUrl
        }))} data-id="element-530" data-mp="1ibdqto64nw9o" />
        </span>
      </div>

      <div className="overflow-x-auto rounded-lg border border-line" data-id="element-531" data-mp="6pe0kxd5mdw7">
        <table className="w-full min-w-[1220px] border-collapse text-[13.5px]" data-id="element-532" data-mp="2ibza4bxuuzyo">
          <thead className="bg-surface" data-id="element-533" data-mp="2ie29hmkwe5g2">
            <tr data-id="element-534" data-mp="2nbbyj14dkf3d">
              {sortTh('model', 'Model')}
              {sortTh('lab', 'Lab')}
              {sortTh('country', 'Country')}
              {sortTh('released', 'Released')}
              <th scope="col" className="border-b border-line px-3 py-2.5 text-left text-[12px] font-normal text-muted" data-id="element-535" data-mp="2dkpornjcaisl">Parameters</th>
              {sortTh('context', 'Context')}
              <th scope="col" className="border-b border-line px-3 py-2.5 text-left text-[12px] font-normal text-muted" data-id="element-536" data-mp="23n36qs6qub4e">License</th>
              <th scope="col" className="border-b border-line px-3 py-2.5 text-left text-[12px] font-normal text-muted" data-id="element-537" data-mp="1po3k1q2sk6av">Modality</th>
              <th scope="col" className="border-b border-line px-3 py-2.5 text-left text-[12px] font-normal text-muted" data-id="element-538" data-mp="1umwuf9ms7h6t">Notable capability</th>
            </tr>
          </thead>
          <tbody data-id="element-539" data-mp="3ozd1gcneizcq">
            {rows.map(m => {
            const cc = modelCountry(m);
            return <tr key={m.model} className="border-b border-line/60 last:border-b-0 hover:bg-surface" data-id="element-540" data-mp="3nktrmqn2f694">
                  <td className="whitespace-nowrap px-3 py-2.5 text-ink" data-id="element-541" data-mp="3iiuo9grbs9c6">
                    {m.model}
                    <SourceRef href={m.sourceUrl} label={`${m.model} announcement`} grade="A" figure={m.model} data-id="element-542" data-mp="3dlbb6k6d62lt" />
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-muted" data-id="element-543" data-mp="a42x8berbmwd">{m.lab}</td>
                  <td className="whitespace-nowrap px-3 py-2.5" data-id="element-544" data-mp="559hecbzzjt1">
                    {cc ? <Link to={`/countries/${cc}`} className="inline-flex items-center gap-2 text-muted hover:text-machine" data-id="element-545" data-mp="57lzcphr9u63">
                        {cca2Of[cc] && <img src={`https://flagcdn.com/${cca2Of[cc]}.svg`} alt="" loading="lazy" className="h-3 w-4 rounded-[2px] object-cover ring-1 ring-line" data-id="element-546" data-mp="3rjsve7x28wyz" />}
                        {countryNames[cc] ?? cc}
                      </Link> : <span className="text-faint" data-id="element-547" data-mp="wcrlam9is0kr">—</span>}
                  </td>
                  <td className={`whitespace-nowrap px-3 py-2.5 font-mono text-[12.5px] ${m.released ? 'text-ink' : 'text-faint'}`} data-id="element-548" data-mp="10e3a4pkkkhrw">{formatReleased(m.released)}</td>
                  <td className={`whitespace-nowrap px-3 py-2.5 font-mono text-[12.5px] ${m.params.startsWith('undisclosed') ? 'text-faint' : 'text-ink'}`} data-id="element-549" data-mp="15c4wbjdakx7l">{m.params}</td>
                  <td className={`whitespace-nowrap px-3 py-2.5 font-mono text-[12.5px] ${m.context === 'undisclosed' ? 'text-faint' : 'text-ink'}`} data-id="element-550" data-mp="1faud18tgcbm9">{m.context}</td>
                  <td className="whitespace-nowrap px-3 py-2.5" data-id="element-551" data-mp="1o47gh4p56vml">
                    <span className={`rounded border px-1.5 py-0.5 font-mono text-[10.5px] ${LICENSE_STYLE[m.license]}`} title={m.licenseName} data-id="element-552" data-mp="1o60mchrxlbtj">
                      {LICENSE_LABEL[m.license]}
                    </span>
                    <span className="ml-2 text-[11.5px] text-faint" data-id="element-553" data-mp="2317wwc1vv45r">{m.licenseName}</span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5 capitalize text-muted" data-id="element-554" data-mp="2obwokhbojhe7">{m.modality}</td>
                  <td className="px-3 py-2.5 text-ink/85" data-id="element-555" data-mp="2jdldy4dk92ux">{m.capability}</td>
                </tr>;
          })}
            {rows.length === 0 && <tr data-id="element-556" data-mp="2xaqpukzygvy3">
                <td colSpan={9} className="px-3 py-10 text-center text-muted" data-id="element-557" data-mp="2sdqjsa33vt6z">
                  No models match these filters.
                </td>
              </tr>}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-[12px] text-muted" data-id="element-558" data-mp="20ndepxczewk0">Country is the releasing lab’s headquarters, e.g. Google DeepMind → United Kingdom.</p>
    </div>;
}